var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * VA-28040 — identifies THIS page load to the control plane.
 *
 * A reload is a new page whose predecessor is still, briefly, uploading parts.
 * The server cannot tell that apart from the same page calling `init` twice
 * (a retry, a double-mount) by anything it can observe on its own, and the two
 * need opposite handling: the first must get its own upload so the dying page's
 * stragglers cannot overwrite it, the second must get the SAME one back.
 *
 * Generated once per module evaluation, so it is stable for the lifetime of the
 * page and necessarily different after a reload — which is exactly the
 * distinction being drawn.
 */
const PAGE_SESSION_ID = (() => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    }
    catch (_a) {
        /* fall through to the non-crypto id below */
    }
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
})();
export const pageSessionId = () => PAGE_SESSION_ID;
const jsonHeaders = { 'Content-Type': 'application/json' };
export class HttpRecordingUploadClient {
    constructor(opts) {
        this.opts = opts;
    }
    url(path) {
        return `${this.opts.apiBaseUrl}/interviews/${this.opts.slug}/recording/upload/${path}`;
    }
    post(path, body) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const res = yield fetch(this.url(path), {
                method: 'POST',
                headers: jsonHeaders,
                // The token travels in the body because the backend reads it there.
                body: JSON.stringify(Object.assign(Object.assign({}, body), { token: this.opts.token })),
            });
            if (!res.ok) {
                let detail = '';
                try {
                    detail = (_b = (_a = ((yield res.json()))) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : '';
                }
                catch (_c) {
                    /* a non-JSON error body is not worth a second failure */
                }
                throw new Error(`recording upload ${path} failed: HTTP ${res.status} ${detail}`.trim());
            }
            return (yield res.json());
        });
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // The key is NOT sent: the server derives it from the authenticated
            // interview. A client-chosen key is a cross-candidate write. The CONTAINER
            // is sent, because the object must be named and typed for what is actually
            // in it — but the server validates it against a closed set and builds the
            // key itself, so this widens nothing.
            const container = (_a = this.opts.container) !== null && _a !== void 0 ? _a : 'webm';
            return this.post('init', {
                container,
                contentType: container === 'mp4' ? 'video/mp4' : 'video/webm',
                // VA-28040: lets the server tell a reload from this page asking twice.
                sessionId: PAGE_SESSION_ID,
            });
        });
    }
    partUrls(uploadId, partNumbers) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            // VA-28132 D3: the duration rides along here, not only on `complete`.
            // `complete` is the ONLY place it used to reach the backend, and a page
            // that dies mid-recording never gets there — which is the reconnect case by
            // definition, so precisely the segments whose length matters most were the
            // ones that never reported one. `part-urls` already happens per part and
            // already carries `uploadId`, so this costs no extra request and bounds the
            // error at one part interval.
            const elapsed = (_b = (_a = this.opts).elapsedSec) === null || _b === void 0 ? void 0 : _b.call(_a);
            const res = yield this.post('part-urls', Object.assign({ uploadId,
                partNumbers }, (typeof elapsed === 'number' && elapsed > 0 ? { durationSec: elapsed } : {})));
            // The backend reports a TTL in seconds; the uploader wants an absolute
            // instant so it can reason about a URL it is holding.
            const expiresAt = typeof res.expiresIn === 'number'
                ? new Date(Date.now() + res.expiresIn * 1000).toISOString()
                : undefined;
            return ((_c = res.urls) !== null && _c !== void 0 ? _c : []).map((u) => (Object.assign(Object.assign({}, u), { expiresAt })));
        });
    }
    complete(uploadId, parts, durationSec) {
        return __awaiter(this, void 0, void 0, function* () {
            // `parts` is a CROSS-CHECK only. The backend reconciles against S3
            // ListParts, which is authoritative — a part can be stored while its
            // acknowledgement is lost, so this list may be wrong in either direction.
            //
            // `durationSec` is `SharedRecorder.elapsedSec` — RECORDED media time, which
            // subtracts `pausedTotalMs`. It is NOT wall clock, and the difference is
            // large: upload backpressure pauses the recorder, and on interview-1d4a8046
            // the two diverged by 49.9s. The container carries no duration of its own
            // (MediaRecorder writes a streaming header), so this is the only source.
            // Since VA-28132 D3 it is also sent on every `part-urls`, so a page that
            // dies before `complete` still reports one.
            return this.post('complete', Object.assign({ uploadId,
                parts }, (durationSec && durationSec > 0 ? { durationSec } : {})));
        });
    }
    abort(uploadId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.post('abort', { uploadId });
        });
    }
    heartbeat(uploadId, lastPart) {
        return __awaiter(this, void 0, void 0, function* () {
            // Keeps the stale-upload sweep from reaping an upload that is simply slow.
            yield this.post('heartbeat', { uploadId, lastPart });
        });
    }
}
